import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';

import { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';

import Model from 'umg-ember-backend/models/variant-model';
import {
  buildValidations,
  disabledWhenVariant,
  validator,
} from 'umg-ember-backend/utils/validations';

const Validations = buildValidations({
  type: [
    validator('belongs-to'),
    validator('presence', {
      presence: true,
      disabled: disabledWhenVariant(),
    }),
  ],
  locationPartners: [
    validator('has-many'),
    validator('presence', {
      presence: true,
      disabled: disabledWhenVariant(),
    }),
  ],
  startDate: validator('presence', {
    presence: true,
    disabled: disabledWhenVariant(),
  }),
  secret: validator('inclusion', {
    in: [false],
    disabled: readOnly('model.isNotPublished'),
    allowBlank: true,
    messageKey: 'forms.live-event.messages.secret',
  }),
  tbc: validator('inclusion', {
    in: [false],
    disabled: readOnly('model.isNotPublished'),
    allowBlank: true,
    messageKey: 'forms.live-event.messages.tbc',
  }),
});

export const settings = {
  copyableOptions: {
    ignoreAttributes: [
      'channelsAdditive',
      'channelsExcluded',
      'channelsExclusive',
      'ownedByChannel',
    ],
  },
  variantProperties: [
    'cmsLanguageId',
    'seoTitle',
    'seoDescription',
    'keywords',
    'seoImage',
    'cmsState',
    'headline',
    'repertoire',
    'text',
  ],
};

export default class LiveEventModel extends Model.extend(Validations) {
  @attr('boolean') cancelled;
  @attr('string') city;
  @attr('string') country;
  @attr('boolean') dgg;
  @attr('string') endDate;
  @attr('string') headline;
  @attr('string') internalRemarks;
  @attr('string') manualLocation;
  @attr('string') repertoire;
  @attr('boolean') secret;
  @attr('string') shopLink;
  @attr startDate;
  @attr startTime;
  @attr('boolean') tbc;
  @attr('string') text;
  @attr('boolean') ticketAvailability;

  // CMS fields
  @attr('number', { defaultValue: 2 }) cmsState;
  @attr('string') seoTitle;
  @attr('string') seoDescription;
  @attr('string') keywords;

  // Meta fields
  @attr cmsCreated;
  @attr cmsDeleted;
  @attr cmsLocktime;
  @attr cmsModified;
  @attr() cmsHistory;
  @belongsTo('user', { async: true, inverse: null }) cmsOwnerId;
  @belongsTo('user', { async: true, inverse: null }) cmsCreatorId;
  @belongsTo('user', { async: true, inverse: null }) cmsLockerId;
  @belongsTo('user', { async: true, inverse: null }) cmsModifierId;

  // Relations
  @hasMany('artist', { async: true, inverse: null }) artists;
  @hasMany('channel', { async: true, inverse: null }) channelsAdditive;
  @hasMany('channel', { async: true, inverse: null }) channelsExcluded;
  @hasMany('channel', { async: true, inverse: null }) channelsExclusive;
  @belongsTo('asset', { async: true, inverse: null }) seoImage;
  @hasMany('live-partner', { async: true, inverse: null }) locationPartners;
  @belongsTo('channel', { async: true, inverse: null }) ownedByChannel;
  @hasMany('live-partner', { async: true, inverse: null }) presenterPartners;
  @belongsTo('category', { async: true, inverse: null }) type;

  // Getter and setter
  get displayHeadline() {
    let location = this.locationPartners[0];
    let country = location?.country.get('name');
    let fallback = `${location?.name}, ${location?.city}, ${country}`;

    if (this.headline) {
      return this.headline;
    }

    return location ? fallback : null;
  }

  get displayLabel() {
    return `${this.displayHeadline} (${this.id})`;
  }

  @computed('cmsState')
  get isNotPublished() {
    return this.cmsState !== 2;
  }
}
